import {
  ApolloLink,
  ApolloProvider,
  ApolloClient as Client,
  HttpLink,
  InMemoryCache,
  from,
  split,
  type Operation
} from '@apollo/client';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import {
  onAuthErrorRedirectLink,
  setAccessTokenLink,
  useAuth
} from '@payflip/web/auth';
import { createClient as createWsClient } from 'graphql-ws';

import { ReactNode } from 'react';

type Props = {
  children: ReactNode;
  uri: string;
  name?: string;
  links?: ApolloLink[];
  enableSubscriptions?: boolean;
};

export function ApolloClient({
  children,
  uri,
  name = 'react-client',
  links = [],
  enableSubscriptions = false
}: Props): JSX.Element {
  const { getAccessToken } = useAuth();

  let link = from([
    setAccessTokenLink(getAccessToken),
    onAuthErrorRedirectLink(),
    ...links,
    new HttpLink({ uri })
  ]);

  if (enableSubscriptions) {
    const isSubscriptionOperation = ({ query }: Operation): boolean => {
      const definition = getMainDefinition(query);
      return (
        definition.kind === 'OperationDefinition' &&
        definition.operation === 'subscription'
      );
    };

    const wsLink = new GraphQLWsLink(
      createWsClient({
        url: uri.replace(/^http/, 'ws'),
        connectionParams: async () => {
          const token = await getAccessToken();
          return { authorization: `Bearer ${token}` };
        }
      })
    );

    // Use the WebSocket link for subscriptions and the HTTP link for all other operations
    link = split(isSubscriptionOperation, wsLink, link);
  }

  const client = new Client({ name, link, cache: new InMemoryCache() });

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
}
